import { trpc } from '@/main'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { jobIntegrantsSource } from '@/hooks/use-job'
import { Suspense, useEffect, useState } from 'react'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { reportSearchDeps, reportQueryOptions } from '@/hooks/use-report'
import { deriveProfiles } from '@/hooks/use-root-search'
import { OfferDialog } from './-components/companies/offer-dialog'

/**
 * Layout for a selected offer (`/offers/$offerId/*`). This route renders real content (the
 * incomplete-offer auto-open + the animated tab Outlet), so per AGENTS.md it OWNS the offerId-keyed
 * prefetches its subtree reads — the persistent chrome (getJob + applicants list) and the report data
 * shared by all three tab children. The report/board/distribution leaves keep only their own
 * tab-specific queries (board/distribution: the applicant indicators).
 *
 * Shared by the company and applicant offer views (both render `<Outlet/>` → this route). The auto-open
 * is owner-gated, so it stays inert for applicants.
 *
 * The `AnimatedOutlet` now lives in the parent `offers.tsx` (wrapping the `<Outlet/>` that renders this
 * layout) so the enter animation plays once when the report panel first appears and never re-animates on
 * tab/offer switches; this layout renders a plain `<Outlet/>` for its tab children. The auto-open's
 * dismiss state still persists across tab AND offer switches because this layout stays mounted.
 */
export const Route = createFileRoute('/_app/offers/$offerId')({
  // EXACTLY the search this layout's context reads. `reportSearchDeps` already covers `first`/`second`/
  // `type` (the report keys); `o`/`od` are the list's own sort. Anything else changing must not re-run it.
  loaderDeps: ({ search }) => ({ ...reportSearchDeps(search), o: search.o, od: search.od, panelQ: search.panelQ }),
  context: ({ deps, params }) => ({
    // The detail every pane on this screen shows. The chrome ABOVE this match (breadcrumb, screen
    // title, the integrants panel) reads THIS field off the match rather than rebuilding the key.
    jobQueryOptions: trpc.jobs.getJob.queryOptions({ jobId: Number(params.offerId) }),
    // The applicants list keyed on the pane's OWN search param. The panel that renders it sits in the
    // parent `offers.tsx`, above this match, so it reads THIS field off the match — its search box writes
    // `panelQ` (not `q`, which the rail beside it owns). It carries the applicant's own `viewerMatch`.
    jobIntegrantsInfiniteQueryOptions: trpc.jobs.getJobIntegrants.infiniteQueryOptions(
      {
        jobId: Number(params.offerId),
        type: deps.type,
        o: deps.o,
        od: deps.od,
        q: deps.panelQ,
        source: jobIntegrantsSource(deriveProfiles(deps, params, false).first),
      },
      { getNextPageParam: (lastPage) => lastPage.nextCursor },
    ),
    reportQueryOptions: reportQueryOptions(deps, params),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.jobQueryOptions).catch(noop)
    context.queryClient.infiniteQuery(context.jobIntegrantsInfiniteQueryOptions).catch(noop)
    if (context.reportQueryOptions) context.queryClient.query(context.reportQueryOptions).catch(noop)
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent offers.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  return (
    <>
      <Suspense fallback={null}>
        <IncompleteOfferAutoOpen />
      </Suspense>
      <Outlet />
    </>
  )
}

/**
 * Auto-opens the completion dialog for an incomplete offer the viewer owns. Reads `getJob` via suspense
 * (loader-prefetched above), inside its own <Suspense fallback={null}> so a cold read never blanks the
 * tab Outlet. Dismiss state is local to this layout — it persists across tab switches (the layout stays
 * mounted) and across offer switches (same route, param change), resetting only when the selection is
 * cleared (back to the `/offers` list), which is the intended session scope.
 */
function IncompleteOfferAutoOpen() {
  const { data: offer } = useSuspenseQuery(Route.useRouteContext().jobQueryOptions)
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set())

  // Once an offer is complete it can never auto-open, so drop any stale dismiss flag (matters only if
  // it later becomes incomplete again).
  useEffect(() => {
    if (offer.fullyRegistered) {
      // oxlint-disable-next-line react/set-state-in-effect -- external data sync: drops a stale dismiss flag when the offer's fetched data flips to fullyRegistered
      setDismissed((prev) => {
        if (!prev.has(offer.id)) return prev
        const next = new Set(prev)
        next.delete(offer.id)
        return next
      })
    }
  }, [offer])

  const shouldOpen = offer.viewerRole === 'owner' && !offer.fullyRegistered && !dismissed.has(offer.id)

  // Mount the dialog on first open and keep it mounted, driving visibility through `open` — a bare
  // `if (!shouldOpen) return null` would unmount it on dismiss and skip the exit animation.
  //
  // Latched DURING render rather than in an effect: an effect needs a second commit before `mounted` is
  // true, so the dialog appeared one paint late on the very first open. This mounts it in the same commit
  // that first computes `shouldOpen`.
  const [mounted, setMounted] = useState(false)
  if (shouldOpen && !mounted) setMounted(true)

  if (!mounted) return null

  return (
    <OfferDialog
      offerId={offer.id}
      open={shouldOpen}
      onOpenChange={(open) => {
        if (!open) setDismissed((prev) => new Set(prev).add(offer.id))
      }}
    >
      <span />
    </OfferDialog>
  )
}
